import { createContext, useContext, type ReactNode } from 'react'
import type { SettledRecordAccess } from './useRecordAccess'
import type { PermissionSetEntity } from '@omnea-app/authz'

const RecordAccessContext = createContext<SettledRecordAccess<PermissionSetEntity> | undefined>(undefined)

type Props = {
  access: SettledRecordAccess<PermissionSetEntity>
  children: ReactNode
}

export const RecordAccessProvider = ({ access, children }: Props) => (
  <RecordAccessContext.Provider value={access}>{children}</RecordAccessContext.Provider>
)

const isAccessTo = <Entity extends PermissionSetEntity>(
  access: SettledRecordAccess<PermissionSetEntity>,
  entity: Entity
): access is SettledRecordAccess<Entity> => access.entity === entity

/**
 * The access the route gate settled for the record in the URL. A component showing another record
 * calls `useRecordAccess` for it instead. The type cannot catch a component reading another family's
 * provider, so the mismatch throws.
 */
export const useRecordAccessContext = <Entity extends PermissionSetEntity>(entity: Entity): SettledRecordAccess<Entity> => {
  const access = useContext(RecordAccessContext)
  if (access == null) throw new Error('useRecordAccessContext must be used within a RecordAccessProvider')
  if (!isAccessTo(access, entity)) {
    throw new Error(`useRecordAccessContext asked for ${entity} inside a ${access.entity} provider`)
  }
  return access
}
